import React, { useState } from "react";
import PlaydateInviteCreationView from "../view/PlaydateInviteCreationView";
import PlaydateInviteCreationCompletionView from "../view/PlaydateInviteCreationCompletionView";
import { DraftPlaydateInvite } from "../types";
import {
    PlaydateInvite,
    PlaydateGuestType,
    useCreatePlaydateInviteMutation,
    Connection,
    ParticipantSummary,
} from "../../../gql/types";
import { convertSelectedDayPartsToScheduleOptionInputs, convertSuggestionToDraftInvite } from "../../../service/playdate/converters/converter";
import { showToast } from "../../../toast/toast";
import PortalRoot from "../../portal/PortalRoot";
import { OverlayOpacity } from "../../portal/types";
import { useLayout } from "../../layout/LayoutContext";
import { appEvents, AppEventType } from "../../../events/app";
import { useGenerateCreateInviteSuggestions } from "service/playdate/requests/useGenerateCreateInviteSuggestion";
import { useUser } from "contexts/UserContext";
import PlaydateInviteContextIngestionView from "../view/PlaydateInviteContextIngestionView";
import { useToastContext } from "contexts/ToastContext";

interface FeatureParameters {
    date?: string;
    connectionIDs?: string[];
    eventID?: string;
}

interface WorkflowProps {
    onBack: () => void;
    onShare?: (invite: PlaydateInvite) => void;
    initialConnections?: ParticipantSummary[];
    featureParameters: FeatureParameters;
    baseZIndex?: number;
    /**
     * Optional override for the create logic — useful for Storybook or mocking.
     */
    onCreate?: (draft: DraftPlaydateInvite) => Promise<PlaydateInvite>;
    onDataChange: (success: boolean, invite?: PlaydateInvite) => void;
}

type CreationStage = "IngestContext" | "EditInvite" | "CreationComplete";

const PlaydateInviteOrganizerCreationWorkflow: React.FC<WorkflowProps> = ({
    onBack,
    onShare,
    initialConnections,
    featureParameters,
    baseZIndex,
    onCreate,
    onDataChange,
}) => {
    const [stage, setStage] = useState<CreationStage>("IngestContext");
    const [createdInvite, setCreatedInvite] = useState<PlaydateInvite | null>(null);
    const [createPlaydateInvite] = useCreatePlaydateInviteMutation();
    const { baseZIndex: layoutZIndex } = useLayout();
    const [draftInvite, setDraftInvite] = useState<DraftPlaydateInvite | null>(null);
    const [userMessage, setUserMessage] = useState<string | null>(null);
    const { user: loggedInUser } = useUser();
    const { triggerToastEvent } = useToastContext();

    const { generateCreateInviteSuggestions, loading: suggestionLoading } = useGenerateCreateInviteSuggestions();

    if (!baseZIndex) {
        baseZIndex = layoutZIndex + 1;
    }
    const handleUserMessageSubmit = (message: string) => {
        // If message remained the same, no need to re-run
        message = message.trim();
        if (message === userMessage) {
            setStage("EditInvite");
            return;
        }
        setUserMessage(message);

        generateCreateInviteSuggestions({
            date: featureParameters?.date,
            connectionIDs: featureParameters?.connectionIDs,
            userMessage: message,
            eventID: featureParameters?.eventID,
            onCompletion: (success, suggestions) => {
                if (!success) {
                    // still proceed to the invite with partially filled out fields
                    const draft = convertSuggestionToDraftInvite(null, loggedInUser?.family?.id ?? "", initialConnections, featureParameters.date);
                    setDraftInvite(draft);
                    setStage("EditInvite");
                }
                if (success && suggestions && suggestions.length > 0) {
                    const firstSuggestion = suggestions[0];
                    const draft = convertSuggestionToDraftInvite(firstSuggestion, loggedInUser?.family?.id ?? "", initialConnections, featureParameters.date);
                    setDraftInvite(draft);
                    setStage("EditInvite");
                }
                triggerToastEvent();
            },
        });
    };

    // Still set the minimal parameters
    const handleOnSkip = () => {
        const draft = convertSuggestionToDraftInvite(null, loggedInUser?.family?.id ?? "", initialConnections, featureParameters.date);
        setDraftInvite(draft);
        setStage("EditInvite");
    }

    const handleContinue = async (draft: DraftPlaydateInvite, setLoading: (loading: boolean) => void) => {
        try {
            setLoading(true);
            if (onCreate) {
                const result = await onCreate(draft);
                setCreatedInvite(result);
                setStage("CreationComplete");
                return;
            }

            const input = {
                title: draft.title,
                location: draft.location,
                playdateType: draft.playdateType,
                optionType: draft.optionType,
                scheduleOptions: convertSelectedDayPartsToScheduleOptionInputs(draft.scheduleOptions),
                rsvpDeadline: draft.rsvpDeadline,
                details: draft.details,
                guestType: PlaydateGuestType.Family,
                guestIDs: draft.guestConnections.map((c) => c.id),
                externalGuests: draft.externalGuests,
                organizerID: draft.organizerID,
            };

            const { data } = await createPlaydateInvite({
                variables: {
                    input: input,
                },
            });

            if (data?.createPlaydateInvite) {
                // We do not push toast message here because we go to the Completion view, toast message would be redundant
                setCreatedInvite(data.createPlaydateInvite);
                setStage("CreationComplete");
            } else {
                throw new Error("No invite returned.");
            }
        } catch (error: any) {
            appEvents.emit(AppEventType.RESET_ACTION_BUTTON);
            showToast("error", "Failed to create invite. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PortalRoot
            baseZIndex={baseZIndex}
            overlayOpacity={OverlayOpacity.COVERED}
        >
            {stage === "IngestContext" && (
                <PlaydateInviteContextIngestionView
                    onSubmit={handleUserMessageSubmit}
                    onSkip={handleOnSkip}
                    loading={suggestionLoading}
                    existingMessage={userMessage ?? undefined}
                    onBack={onBack}
                />
            )}

            {stage === "EditInvite" && (
                <PlaydateInviteCreationView
                    mode={"create"}
                    onBack={() => setStage("IngestContext")}
                    onContinue={handleContinue}
                    baseZIndex={baseZIndex}
                    initialGuests={initialConnections}
                    initialDraft={draftInvite !== null ? draftInvite : undefined}
                />
            )}

            {stage === "CreationComplete" && createdInvite && (
                <PlaydateInviteCreationCompletionView
                    baseZIndex={baseZIndex}
                    invite={createdInvite}
                    onBack={() => {
                        onDataChange(true, createdInvite);
                        onBack();
                    }}
                    onShare={onShare}
                />
            )}
        </PortalRoot>
    );
};

export default PlaydateInviteOrganizerCreationWorkflow;
